import React from 'react';
import { Helmet } from "react-helmet";
import { Link } from 'react-router-dom';
import {
  TruckIcon,
  BoltIcon,
  CubeTransparentIcon
} from '@heroicons/react/24/outline';

const services = [
  {
    name: 'Godstransport',
    description: 'Säker och effektiv transport av gods inom hela Norden.',
    href: '/services/godstransport',
    icon: TruckIcon,
  },
  {
    name: 'Expressleveranser',
    description: 'Snabba leveranser när tiden är kritisk, med full kontroll.',
    href: '/services/expressleveranser',
    icon: BoltIcon,
  },
  {
    name: 'Specialtransporter',
    description: 'Skräddarsydda lösningar för känsligt, tungt eller skrymmande gods.',
    href: '/services/specialtransporter',
    icon: CubeTransparentIcon,
  },
];

const ServicesSection = () => {
  return (
    <section
      aria-labelledby="services-heading"
      className="bg-white px-6 py-20 lg:px-8"
    >
      {/* SEO FOR /services PAGE */}
      <Helmet>
        <title>Tjänster | Nordic Route</title>
        <meta
          name="description"
          content="Nordic Route erbjuder godstransport, expressleveranser och specialtransporter i hela Norden. Se våra tjänster och hitta rätt logistiklösning."
        />
        <link rel="canonical" href="https://www.nordicroute.se/services" />

        {/* STRUCTURED DATA: ItemList for SEO */}
        <script type="application/ld+json">
          {`
            {
              "@context": "https://schema.org",
              "@type": "ItemList",
              "name": "Nordic Route - Tjänster",
              "itemListElement": [
                {
                  "@type": "ListItem",
                  "position": 1,
                  "name": "Godstransport",
                  "url": "https://www.nordicroute.se/services/godstransport"
                },
                {
                  "@type": "ListItem",
                  "position": 2,
                  "name": "Expressleveranser",
                  "url": "https://www.nordicroute.se/services/expressleveranser"
                },
                {
                  "@type": "ListItem",
                  "position": 3,
                  "name": "Specialtransporter",
                  "url": "https://www.nordicroute.se/services/specialtransporter"
                }
              ]
            }
          `}
        </script>
      </Helmet>

      <div className="mx-auto max-w-6xl">
        <div className="max-w-2xl">
          <h2
            id="services-heading"
            className="text-sm font-semibold uppercase tracking-[0.25em] text-indigo-700/80"
          >
            Våra tjänster
          </h2>

          <p className="mt-3 text-3xl font-semibold tracking-tight text-slate-900 sm:text-4xl">
            Logistiklösningar anpassade för Norden.
          </p>

          <p className="mt-4 text-sm text-slate-600 sm:text-base">
            Nordic Route kombinerar pålitliga transporter med modern teknik för att ge dig kontroll,
            transparens och trygghet i varje leverans.
          </p>
        </div>

        {/* SERVICE CARDS */}
        <div className="mt-10 grid gap-6 md:grid-cols-3">
          {services.map((service) => {
            const Icon = service.icon;

            return (
              <article
                key={service.name}
                className="group flex flex-col justify-between rounded-2xl border border-slate-100 bg-slate-50/60 p-6 shadow-sm shadow-slate-100/60 ring-1 ring-slate-100 transition hover:-translate-y-1 hover:bg-white hover:shadow-md hover:shadow-indigo-100 hover:ring-indigo-100"
              >
                <div>
                  <div className="inline-flex items-center justify-center rounded-xl bg-indigo-50 p-3 text-indigo-600">
                    <Icon className="h-6 w-6" aria-hidden="true" />
                  </div>

                  <h3 className="mt-5 text-lg font-semibold text-slate-900">
                    {service.name}
                  </h3>

                  <p className="mt-3 text-sm text-slate-600">{service.description}</p>
                </div>

                <div className="mt-6">
                  <Link
                    to={service.href}
                    className="text-sm font-semibold text-indigo-700 transition group-hover:text-indigo-800"
                    aria-label={`${service.name} – Läs mer`}
                  >
                    Läs mer <span aria-hidden="true">→</span>
                  </Link>
                </div>
              </article>
            );
          })}
        </div>
      </div>
    </section>
  );
};

export default ServicesSection;
